import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { Divider } from '@mui/material';
import { Box } from '@mui/system';
import Radio from '@mui/material/Radio';
import CustomButton from '@components/Button/Button';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { makeDefaultPaymentMethod } from '@redux/services/paymentMethod';
import { setCustomAlert } from '@redux/actions/general';
import { useDispatch } from 'react-redux';
import LoadingState from '@components/Loader/LoadingState';

export default function PaymentFailed({
  paymentMethods,
  defaultCardID,
  setActive,
  setShowPaymentMethod,
}) {
  const [selectedCard, setSelectedCard] = useState(defaultCardID || '');
  const [loadingStates, setLoadingStates] = useState({});
  const contRef = useRef();
  useEffect(() => {
    const object = paymentMethods?.reduce(
      (acc, curr) => ({ ...acc, [curr?._id]: { load: false, default: false } }),
      {}
    );
    setLoadingStates({ ...object });
  }, []);
  const dispatch = useDispatch();
  const handleChange = (e) => {
    const id = e.target.value;
    setLoadingStates({
      ...loadingStates,
      [id]: { load: true, default: false },
    });
    dispatch(makeDefaultPaymentMethod(id, paymentMethods))
      .then(() => {
        setSelectedCard(id);
        contRef.current.scrollTo(0, 0);
      })
      .catch(() => {
        dispatch(
          setCustomAlert({
            message: 'Something went wrong while making card default!',
            state: true,
            type: 'error',
          })
        );
      })
      .finally(() =>
        setLoadingStates({
          ...loadingStates,
          [id]: { load: false, default: true },
        })
      );
  };

  useEffect(() => {
    setSelectedCard(defaultCardID);
  }, [defaultCardID]);

  return (
    <div className='text-center'>
      <div className='flex justify-center'>
        <Image
          width='96px'
          height='96px'
          alt='alert'
          src='/assets/payment-alert.svg'
        />
      </div>
      <div className='font-bold text-[20px] pt-3 dark:text-white'>
        Payment Failed!
      </div>
      <div className='pt-2 pb-3'>
        Your attempt to purchase extra User Seat(s) failed. Please update your
        payment method and try again.
      </div>
      <Divider />
      <div className='pt-3 text-left font-semibold'>
        Please select from one of your cards:
      </div>
      <Box
        className='overflow-y-scroll scroll-smooth rounded-md w-full dark:border-1 dark:border-custom-border'
        border='1px solid #EBEBEF'
        maxHeight='214px'
        paddingX='12px'
        ref={contRef}
      >
        {paymentMethods?.map((card) => (
          <>
            <Box
              className=' p-3 rounded-md mt-[12px]   w-full dark:border-1 dark:border-custom-border'
              border={`1px solid ${
                card?.isFailed
                  ? 'red'
                  : loadingStates?.[card?._id]?.default
                  ? 'green'
                  : '#EBEBEF'
              }`}
            >
              <div className='flex'>
                {loadingStates?.[card?._id]?.load ? (
                  <div className='flex justify-center items-center w-12'>
                    <LoadingState />
                  </div>
                ) : (
                  <Radio
                    checked={selectedCard === card?._id}
                    name='card'
                    value={card?._id}
                    onChange={handleChange}
                  />
                )}

                <div className='w-full'>
                  <div className='flex items-center justify-between'>
                    <div className='font-bold text-xs'>CARD</div>
                    <div className='font-semibold text-xs'>EXP</div>
                  </div>
                  <div className='flex items-center justify-between pt-2'>
                    <div className='flex items-center'>
                      <img src='/assets/visa.svg' alt='visa' />
                      <div className=' pl-2 flex items-center'>
                        <div className='pr-2 pt-2'>****</div>
                        <div className='pr-2 pt-2'>****</div>
                        <div className='pr-2 pt-2'>****</div>
                        <div className='pr-3 font-bold'>{card?.last4}</div>
                      </div>
                    </div>
                    <div className='font-semibold'>
                      {card?.expMonth}/{card?.expYear.toString().slice(2)}
                    </div>
                  </div>
                </div>
              </div>
            </Box>
            <div className='pb-[12px]' />
          </>
        ))}
      </Box>
      <div className='py-3 flex'>
        <div className='w-[35%]'>
          <CustomButton
            fullWidth
            btnColor='outlined'
            onClick={() => setActive(5)}
          >
            <AddIcon /> Add New
          </CustomButton>
        </div>
        <div className='w-[65%] pl-4'>
          <CustomButton
            fullWidth
            btnColor='blue'
            onClick={() => {
              setShowPaymentMethod(true);
              setActive(1);
            }}
          >
            Continue to pay <KeyboardArrowRightIcon />
          </CustomButton>
        </div>
      </div>
    </div>
  );
}

PaymentFailed.propTypes = {
  paymentMethods: PropTypes.array,
  defaultCardID: PropTypes.string,
  setActive: PropTypes.func,
  setShowPaymentMethod: PropTypes.func,
};
